// Encrypted document store (Implementation Plan v02 §5.1, §6; I-1a Batch 3).
//
// - Every diary record is sealed with the data key (crypto/ envelope: AES-GCM 256
//   + AAD, generic clear kind, size buckets). Screens never call this directly:
//   screen → domain → policy → store.
// - On open (after unlock) every record is decrypted once into an in-memory
//   index; there is no query engine. A record that fails to decrypt or parse is
//   SKIPPED AND COUNTED (for the diagnostic export), never wiped.
// - Single writer: the copy that holds the Web Lock writes; any other copy opens
//   read-only. A newer database, or a failed migration, also opens read-only.
//   Within one copy, writes and deletes run one at a time (no put / delete race).
// - "Storage full": the write is refused, the store stops writing and says so
//   (the screen shows a calm line and offers a backup); nothing is half-written.
//   Deleting stays possible while full — it is how space is freed.
// - close() drops the references to the data key and the decrypted index. Another
//   copy deleting or upgrading the database closes this one the same way.

import {
  type Bytes,
  blobSize,
  fromBase64,
  importPhotoKey,
  newId,
  newPhotoKeyBytes,
  openRecord,
  PHOTO_KEY_ID,
  RecordOpenError,
  sealRecord,
  type SealedRecord,
  toBase64,
} from '../crypto'

import { decodeRecord, type DiaryRecord, encodeRecord, type RecordLinks } from './codec'
import { collectMomentGroup, deleteGroup, destroyEverything } from './deletion'
import { type Migration, type MigrationOutcome, MIGRATIONS, readStoredVersion, runMigrations } from './migrations'
import {
  browserSpaceEstimate,
  DATA_SCHEMA_VERSION,
  DB_NAME,
  isStorageFullError,
  openStoreDb,
  type SpaceEstimate,
  type StoreDb,
  watchDone,
} from './schema'
import { acquireWriterLock, type LockManagerLike, type WriterLock } from './writerLock'

export type ReadOnlyReason = 'other-copy-open' | 'newer-data' | 'migration-failed'

export class StoreFullError extends Error {
  constructor() {
    super('storage is full; writing has stopped')
    this.name = 'StoreFullError'
  }
}

export class StoreReadOnlyError extends Error {
  constructor(readonly reason: ReadOnlyReason) {
    super(`the store is read-only (${reason})`)
    this.name = 'StoreReadOnlyError'
  }
}

export class StoreClosedError extends Error {
  constructor() {
    super('the store is closed')
    this.name = 'StoreClosedError'
  }
}

export interface OpenStoreOptions {
  /** The unwrapped, non-extractable data key (crypto/keys.ts). */
  readonly dataKey: CryptoKey
  readonly dbName?: string
  readonly locks?: LockManagerLike
  /** Defaults to the app's MIGRATIONS; tests pass their own. */
  readonly migrations?: readonly Migration[]
  /** Tests only: pretend the app is at another data-schema version. */
  readonly appSchemaVersion?: number
  /** Asked only after an unclear failed write. Defaults to `navigator.storage`; null = never ask. */
  readonly space?: SpaceEstimate | null
}

export interface NewRecord {
  readonly kind: string
  readonly data: unknown
  readonly links?: RecordLinks
  /** Omit for a new record (random UUID v4); pass to replace an existing one. */
  readonly id?: string
}

export interface StoredBlob {
  readonly blobId: string
  /** Base64 photo key — keep it inside the moment's (encrypted) record data. */
  readonly key: string
}

const NOT_HELD: WriterLock = { held: false, release: () => undefined }

export class EncryptedStore {
  private db: StoreDb | null = null
  private dataKey: CryptoKey | null
  private readonly index = new Map<string, DiaryRecord>()
  private skipped = 0
  private full = false
  /** Writes and deletes run one at a time, so a delete can never race an in-flight put. */
  private queue: Promise<unknown> = Promise.resolve()

  private constructor(
    dataKey: CryptoKey,
    private readonly lock: WriterLock,
    private readonly dbName: string,
    private readonly schemaVersion: number,
    /** Data-schema version of the records in this store (the app's, unless read-only for a version reason). */
    readonly recordsVersion: number,
    readonly readOnlyReason: ReadOnlyReason | null,
    readonly migration: MigrationOutcome | null,
    private readonly space: SpaceEstimate | null,
  ) {
    this.dataKey = dataKey
  }

  static async open(options: OpenStoreOptions): Promise<EncryptedStore> {
    const dbName = options.dbName ?? DB_NAME
    const appVersion = options.appSchemaVersion ?? DATA_SCHEMA_VERSION
    let closeOnBlocking: () => void = () => undefined
    const db = await openStoreDb(dbName, {
      onBlocking: () => closeOnBlocking(),
      onTerminated: () => closeOnBlocking(),
    })
    let lock = NOT_HELD
    try {
      lock = await acquireWriterLock(options.locks)

      let reason: ReadOnlyReason | null = null
      let migration: MigrationOutcome | null = null
      if (lock.held) {
        migration = await runMigrations(db, options.dataKey, appVersion, options.migrations ?? MIGRATIONS)
        if (migration.kind === 'newer-data') {
          reason = 'newer-data'
        } else if (migration.kind === 'failed') {
          reason = 'migration-failed'
        }
      } else {
        // Another copy is the writer (and runs any migration). Never migrate here.
        const stored = await readStoredVersion(db)
        reason = stored !== null && stored > appVersion ? 'newer-data' : 'other-copy-open'
      }

      // The data-schema version the records are at once open (a backup header
      // carries it): the app's after a fresh / up-to-date / migrated open, else
      // the version they were left at.
      let recordsVersion = appVersion
      if (migration?.kind === 'newer-data') {
        recordsVersion = migration.stored
      } else if (migration?.kind === 'failed') {
        recordsVersion = migration.from
      } else if (!lock.held) {
        recordsVersion = (await readStoredVersion(db)) ?? appVersion
      }

      const space = options.space === undefined ? browserSpaceEstimate() : options.space
      const store = new EncryptedStore(options.dataKey, lock, dbName, appVersion, recordsVersion, reason, migration, space)
      store.db = db
      closeOnBlocking = () => store.close()
      await store.load()
      return store
    } catch (error) {
      // Never leave the lock or the connection behind: a retry in this page, or
      // another copy, must be able to open as the writer.
      db.close()
      lock.release()
      throw error
    }
  }

  private async load(): Promise<void> {
    const { db, dataKey } = this.live()
    for (const sealed of await db.getAll('records')) {
      try {
        const record = await decodeRecord(dataKey, sealed)
        this.index.set(record.id, record)
      } catch {
        this.skipped++
      }
    }
  }

  private live(): { db: StoreDb; dataKey: CryptoKey } {
    if (!this.db || !this.dataKey) {
      throw new StoreClosedError()
    }
    return { db: this.db, dataKey: this.dataKey }
  }

  /** A writable copy; `allowWhenFull` for deletes (deleting frees space). */
  private writable(allowWhenFull = false): { db: StoreDb; dataKey: CryptoKey } {
    const live = this.live()
    if (this.readOnlyReason) {
      throw new StoreReadOnlyError(this.readOnlyReason)
    }
    if (this.full && !allowWhenFull) {
      throw new StoreFullError()
    }
    return live
  }

  private serial<T>(task: () => Promise<T>): Promise<T> {
    const run = this.queue.then(task, task)
    this.queue = run.catch(() => undefined)
    return run
  }

  /** `neededBytes`: the size of what the write stores (judges an unclear failure). */
  private async guardQuota<T>(neededBytes: number, write: () => Promise<T>): Promise<T> {
    try {
      return await write()
    } catch (error) {
      if (await isStorageFullError(error, neededBytes, this.space)) {
        this.full = true
        throw new StoreFullError()
      }
      throw error
    }
  }

  get isWritable(): boolean {
    return this.db !== null && this.readOnlyReason === null && !this.full
  }

  /** Records that could not be opened (counted in the diagnostic export). */
  get skippedCount(): number {
    return this.skipped
  }

  get isFull(): boolean {
    return this.full
  }

  /** After the person frees space (or makes a backup), try writing again. */
  resumeWriting(): void {
    this.full = false
  }

  get(id: string): DiaryRecord | undefined {
    this.live()
    return this.index.get(id)
  }

  list(kind?: string): DiaryRecord[] {
    this.live()
    const all = [...this.index.values()]
    return kind === undefined ? all : all.filter((r) => r.kind === kind)
  }

  put(input: NewRecord): Promise<DiaryRecord> {
    return this.serial(async () => {
      const { db, dataKey } = this.writable()
      const id = input.id ?? newId()
      const record: DiaryRecord =
        input.links === undefined
          ? { id, kind: input.kind, data: input.data }
          : { id, kind: input.kind, data: input.data, links: input.links }
      const sealed = await encodeRecord(dataKey, record, this.schemaVersion)
      await this.guardQuota(sealed.ct.byteLength, async () => {
        const tx = db.transaction('records', 'readwrite')
        const done = watchDone(tx)
        await tx.store.put(sealed)
        await done
      })
      if (this.db) {
        this.index.set(id, record) // never after close(): the decrypted index stays empty
      }
      return record
    })
  }

  /** Store a photo under its own new key. Keep the returned key in the moment's data. */
  putBlob(content: Uint8Array): Promise<StoredBlob> {
    return this.serial(async () => {
      const { db } = this.writable()
      const raw = newPhotoKeyBytes()
      const key = toBase64(raw)
      const photoKey = await importPhotoKey(raw)
      raw.fill(0)
      const blobId = newId()
      const sealed = await sealRecord(
        photoKey,
        { id: blobId, kind: 'blob', schemaVersion: this.schemaVersion, keyId: PHOTO_KEY_ID },
        content,
        blobSize,
      )
      await this.guardQuota(sealed.ct.byteLength, async () => {
        const tx = db.transaction('blobs', 'readwrite')
        const done = watchDone(tx)
        await tx.store.put(sealed)
        await done
      })
      return { blobId, key }
    })
  }

  async getBlob(blobId: string, key: string): Promise<Bytes | null> {
    const { db } = this.live()
    const sealed = await db.get('blobs', blobId)
    if (!sealed) {
      return null
    }
    let raw: Bytes
    try {
      raw = fromBase64(key)
    } catch {
      throw new RecordOpenError('malformed photo key')
    }
    try {
      return await openRecord(await importPhotoKey(raw), sealed)
    } catch (error) {
      throw error instanceof RecordOpenError ? error : new RecordOpenError()
    } finally {
      raw.fill(0)
    }
  }

  /**
   * The stored photo blobs with these ids, still sealed under their own photo
   * keys (for the backup file, I-1a Batch 6b). An id with no blob is left out.
   * Read in one transaction; allowed in a read-only copy.
   */
  async sealedBlobs(blobIds: readonly string[]): Promise<SealedRecord[]> {
    const { db } = this.live()
    const tx = db.transaction('blobs', 'readonly')
    const done = watchDone(tx)
    const found = await Promise.all(blobIds.map((id) => tx.store.get(id)))
    await done
    return found.filter((sealed): sealed is SealedRecord => sealed !== undefined && sealed.kind === 'blob' && sealed.keyId === PHOTO_KEY_ID)
  }

  /** Delete one moment with its answers, events, draft and photos — all or nothing. Allowed while full. */
  deleteMoment(momentId: string): Promise<number> {
    return this.serial(async () => {
      const { db } = this.writable(true)
      const group = collectMomentGroup(this.index, momentId)
      if (group.recordIds.length === 0) {
        return 0
      }
      await deleteGroup(db, group)
      for (const id of group.recordIds) {
        this.index.delete(id)
      }
      return group.recordIds.length
    })
  }

  /**
   * Delete everything: keys first (crypto-shredding), then content, then the
   * database. Allowed while full or after a failed migration — never blocked —
   * but not from a read-only second copy. The store is closed afterwards, and
   * the writer lock is released even if a step fails (retry with destroyStore).
   */
  deleteEverything(onBlocked?: () => void): Promise<void> {
    return this.serial(async () => {
      const { db } = this.live()
      if (this.readOnlyReason === 'other-copy-open') {
        throw new StoreReadOnlyError(this.readOnlyReason)
      }
      this.dropState()
      try {
        await destroyEverything(db, this.dbName, onBlocked)
      } finally {
        this.lock.release()
      }
    })
  }

  private dropState(): void {
    this.index.clear()
    this.dataKey = null
    this.db = null
  }

  /**
   * Lock: drop the key and decrypted state, close the connection, release the
   * writer lock. A write already in flight finishes its transaction but no
   * longer updates the (cleared) index.
   */
  close(): void {
    const db = this.db
    this.dropState()
    db?.close()
    this.lock.release()
  }
}
